import React, { useState, useRef } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const EvidenceUpload = ({ 
  attachments, 
  onAttachmentsChange, 
  error,
  className = '' 
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const maxFiles = 5;
  const maxFileSize = 10 * 1024 * 1024; // 10MB
  const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

  const handleFileSelect = (files) => {
    const validFiles = Array.from(files)?.filter(file => {
      if (!allowedTypes?.includes(file?.type)) {
        alert(`File ${file?.name} is not a supported image format`);
        return false;
      }
      if (file?.size > maxFileSize) {
        alert(`File ${file?.name} is too large. Maximum size is 10MB`);
        return false;
      }
      return true;
    });

    if (attachments?.length + validFiles?.length > maxFiles) {
      alert(`You can only upload up to ${maxFiles} files`);
      return;
    }

    const newAttachments = validFiles?.map(file => ({
      id: Date.now() + Math.random(),
      file,
      name: file?.name,
      size: file?.size,
      type: file?.type,
      preview: URL.createObjectURL(file),
      alt: `Screenshot showing service outage issue - ${file?.name}`
    }));

    onAttachmentsChange([...attachments, ...newAttachments]);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragging(false);
    handleFileSelect(e?.dataTransfer?.files);
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    setIsDragging(false);
  };

  const handleFileInputChange = (e) => {
    handleFileSelect(e?.target?.files);
  };

  const removeAttachment = (id) => {
    const updatedAttachments = attachments?.filter(att => att?.id !== id);
    onAttachmentsChange(updatedAttachments);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex items-center space-x-2">
        <Icon name="Camera" size={20} className="text-primary" />
        <h3 className="text-lg font-semibold text-foreground">Evidence (Optional)</h3>
      </div>
      <p className="text-sm text-muted-foreground">
        Upload screenshots, error messages, or other visual evidence of the outage
      </p>
      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          isDragging 
            ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 hover:bg-muted/30'
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <Icon name="Upload" size={32} className="mx-auto mb-4 text-muted-foreground" />
        <p className="text-sm font-medium text-foreground mb-2">
          Drop images here or click to browse
        </p>
        <p className="text-xs text-muted-foreground mb-4">
          PNG, JPG, GIF, WebP up to 10MB each (max {maxFiles} files)
        </p>
        
        <Button
          variant="outline"
          onClick={() => fileInputRef?.current?.click()}
          iconName="FolderOpen"
          iconPosition="left"
        >
          Choose Files
        </Button>
        
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={allowedTypes?.join(',')}
          onChange={handleFileInputChange}
          className="hidden"
        />
      </div>
      {/* Mobile Camera Button */}
      <div className="sm:hidden">
        <Button
          variant="outline"
          fullWidth
          iconName="Camera"
          iconPosition="left"
          onClick={() => {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = 'image/*';
            input.capture = 'environment';
            input.onchange = (e) => handleFileSelect(e?.target?.files);
            input?.click();
          }}
        >
          Take Photo
        </Button>
      </div>
      {/* Uploaded Files */}
      {attachments?.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-foreground">Uploaded Files ({attachments?.length}/{maxFiles})</h4>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {attachments?.map((attachment) => (
              <div key={attachment?.id} className="flex items-center space-x-3 p-3 border border-border rounded-md bg-card">
                <div className="flex-shrink-0 w-12 h-12 rounded-md overflow-hidden bg-muted">
                  <Image
                    src={attachment?.preview}
                    alt={attachment?.alt}
                    className="w-full h-full object-cover"
                  />
                </div>
                
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {attachment?.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatFileSize(attachment?.size)}
                  </p>
                </div>
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeAttachment(attachment?.id)}
                  iconName="X"
                  className="text-error hover:text-error hover:bg-error/10"
                />
              </div>
            ))}
          </div>
        </div>
      )}
      {error && (
        <div className="flex items-center space-x-2 p-3 bg-error/10 border border-error/20 rounded-md">
          <Icon name="AlertCircle" size={16} className="text-error" />
          <span className="text-sm text-error">{error}</span>
        </div>
      )}
    </div>
  );
};

export default EvidenceUpload;